import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { eq, asc, count, sql } from "drizzle-orm"
import { lomExtraQuantityRules } from "@/db/bom-schema"
import { db } from "@/db/db"
import { countryCodeMappings, countries, stores } from "@/db/schema"
import { logAuditEvent } from "@/features/audit-logs/server/auditLogger"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import { resolveDisplayCode } from "./resolveDisplayCode"

export type CountryCodeMapping = typeof countryCodeMappings.$inferSelect

/** "1000-1999", or just "G000" when there is no end range. */
function formatRange(startRange: string, endRange?: string | null): string {
  return endRange ? `${startRange}-${endRange}` : startRange
}

// --- Server Functions ---

export const listCountryCodeMappingsFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async () => {
    const request = getRequest()
    await assertPermission(request, "countryCodeMapping", "read")

    const mappings = await db
      .select()
      .from(countryCodeMappings)
      .orderBy(asc(countryCodeMappings.startRange))
    const allCountries = await db
      .select({ id: countries.id, name: countries.name, code: countries.code })
      .from(countries)
      .orderBy(asc(countries.name))

    return { mappings, countries: allCountries }
  })

export const createCountryCodeMappingFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(
    (data: {
      startRange: string
      endRange?: string | null
      displayCode: string
      countryId?: string | null
      locale?: string | null
    }) => data,
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const user = await assertPermission(request, "countryCodeMapping", "create")

    const [created] = await db
      .insert(countryCodeMappings)
      .values({
        startRange: data.startRange,
        endRange: data.endRange || null,
        displayCode: data.displayCode,
        countryId: data.countryId || null,
        locale: data.locale || null,
        createdBy: user.id,
      })
      .returning()

    await logAuditEvent({
      action: "create",
      entityType: "countryCodeMapping",
      entityId: created.id,
      entityName: data.displayCode,
      summary: `Created country code mapping: ${formatRange(data.startRange, data.endRange)} → ${data.displayCode}`,
    })

    return created
  })

export const updateCountryCodeMappingFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(
    (data: {
      id: string
      startRange: string
      endRange?: string | null
      displayCode: string
      countryId?: string | null
      locale?: string | null
    }) => data,
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "countryCodeMapping", "update")

    // A region's LoM amount rules name its country too (BLU-764): moving the
    // region to another country would leave those rules behind.
    const existing = await db.query.countryCodeMappings.findFirst({
      where: eq(countryCodeMappings.id, data.id),
    })
    if (existing && (existing.countryId ?? null) !== (data.countryId || null)) {
      const [{ rules }] = await db
        .select({ rules: count() })
        .from(lomExtraQuantityRules)
        .where(eq(lomExtraQuantityRules.regionCode, existing.displayCode))
      if (rules > 0) {
        throw new Error(
          `${existing.displayCode} has ${rules} list of materials amount rule${rules === 1 ? "" : "s"} for its region. Remove them on List of Materials → Extras before moving it to another country.`,
        )
      }
    }

    const [updated] = await db
      .update(countryCodeMappings)
      .set({
        startRange: data.startRange,
        endRange: data.endRange || null,
        displayCode: data.displayCode,
        countryId: data.countryId || null,
        locale: data.locale || null,
      })
      .where(eq(countryCodeMappings.id, data.id))
      .returning()

    if (!updated) {
      throw new Error("Country code mapping not found")
    }

    await logAuditEvent({
      action: "update",
      entityType: "countryCodeMapping",
      entityId: data.id,
      entityName: data.displayCode,
      summary: `Updated country code mapping: ${formatRange(data.startRange, data.endRange)} → ${data.displayCode}`,
    })

    return updated
  })

export const deleteCountryCodeMappingFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "countryCodeMapping", "delete")

    const [mapping] = await db
      .select()
      .from(countryCodeMappings)
      .where(eq(countryCodeMappings.id, data.id))
    if (!mapping) return { success: true }

    // A store's region_code FK references display_code (onDelete restrict), so a
    // delete would fail at the DB. Surface a clear message instead.
    const [usage] = await db
      .select({ count: sql<number>`count(*)::int` })
      .from(stores)
      .where(eq(stores.regionCode, mapping.displayCode))
    if (usage.count > 0) {
      throw new Error(
        JSON.stringify({
          error: "Validation error",
          message: `Cannot delete region "${mapping.displayCode}": ${usage.count} store(s) still use it. Reassign those stores first.`,
        }),
      )
    }

    const [deleted] = await db
      .delete(countryCodeMappings)
      .where(eq(countryCodeMappings.id, data.id))
      .returning()

    if (deleted) {
      await logAuditEvent({
        action: "delete",
        entityType: "countryCodeMapping",
        entityId: data.id,
        entityName: deleted.displayCode,
        summary: `Deleted country code mapping: ${formatRange(deleted.startRange, deleted.endRange)} → ${deleted.displayCode}`,
      })
    }

    return { success: true }
  })

export const resolveDisplayCodeFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((data: { branchNumber: string }) => data)
  .handler(async ({ data }) => {
    return resolveDisplayCode(data.branchNumber)
  })
